<template>
  <v-card variant="outlined" class="ai-briefing mb-4">
    <v-card-title class="d-flex align-center text-subtitle-1">
      <v-icon start size="small">mdi-creation-outline</v-icon>
      AI-samenvatting
    </v-card-title>

    <v-card-text>
      <v-skeleton-loader v-if="loading" type="paragraph" />

      <template v-else-if="briefing">
        <p class="text-caption text-medium-emphasis mb-2">
          Gemaakt op {{ formatDate(briefing.generated_at) }} op basis van
          {{ briefing.report_count }}
          {{ briefing.report_count === 1 ? "rapport" : "rapporten" }} sinds
          {{ formatDate(briefing.period_from) }}. Automatisch gemaakt:
          controleer belangrijke punten in de rapporten zelf.
        </p>

        <v-alert
          v-if="briefing.new_reports_since > 0"
          type="info"
          variant="tonal"
          density="compact"
          class="mb-3"
        >
          {{ newReportsLabel }}
        </v-alert>

        <p class="text-body-2 ai-briefing__summary">{{ briefing.summary }}</p>

        <template v-if="briefing.key_points.length">
          <div class="text-subtitle-2 mt-3">Belangrijkste punten</div>
          <AiBriefingKeyPoints
            :points="briefing.key_points"
            :account-id="accountId"
          />
        </template>

        <template v-if="briefing.open_actions.length">
          <div class="text-subtitle-2 mt-3">Nog te doen</div>
          <ul class="ai-briefing__list text-body-2">
            <li v-for="(action, index) in briefing.open_actions" :key="index">
              {{ action }}
            </li>
          </ul>
        </template>

        <template v-if="briefing.attention_points.length">
          <div class="text-subtitle-2 mt-3">Aandachtspunten</div>
          <ul class="ai-briefing__list text-body-2">
            <li
              v-for="(point, index) in briefing.attention_points"
              :key="index"
            >
              {{ point }}
            </li>
          </ul>
        </template>
      </template>

      <p v-else class="text-body-2 text-medium-emphasis">
        Nog geen samenvatting. Een samenvatting gebruikt de rapporten van de
        laatste 90 dagen.
      </p>

      <v-alert
        v-if="error"
        type="warning"
        variant="tonal"
        density="compact"
        class="mt-3"
      >
        {{ error }}
      </v-alert>
    </v-card-text>

    <v-card-actions v-if="!loading" class="flex-wrap">
      <v-btn
        color="primary"
        :variant="briefing && !briefing.new_reports_since ? 'text' : 'flat'"
        prepend-icon="mdi-refresh"
        :loading="generating"
        @click="generate"
      >
        {{ briefing ? "Vernieuwen" : "Samenvatting maken" }}
      </v-btn>
      <v-spacer />
      <template v-if="briefing?.usage_log_id">
        <span class="text-caption mr-1">Nuttig?</span>
        <v-btn
          :icon="rating === 1 ? 'mdi-thumb-up' : 'mdi-thumb-up-outline'"
          variant="text"
          aria-label="Nuttig"
          :aria-pressed="rating === 1"
          @click="rate(1)"
        />
        <v-btn
          :icon="rating === -1 ? 'mdi-thumb-down' : 'mdi-thumb-down-outline'"
          variant="text"
          aria-label="Niet nuttig"
          :aria-pressed="rating === -1"
          @click="rate(-1)"
        />
      </template>
    </v-card-actions>
  </v-card>
</template>

<script setup>
/**
 * The stored AI briefing of an account: a summary of the recent reports, kept on the
 * account. It is only created or refreshed when the worker asks for it; when newer
 * reports exist, the card says so and offers "Vernieuwen".
 */
import { ref, computed, watch } from "vue";
import { useAiStoreRefactor } from "@/stores/aiStoreRefactor";
import { useReportsStoreRefactor } from "@/stores/reportsStoreRefactor";
import AiBriefingKeyPoints from "./AiBriefingKeyPoints.vue";

const props = defineProps({
  accountId: { type: [Number, String], required: true },
});

const aiStore = useAiStoreRefactor();
const reportsStore = useReportsStoreRefactor();
const briefing = ref(null);
const loading = ref(false);
const generating = ref(false);
const error = ref(null);
const rating = ref(null);

const newReportsLabel = computed(() => {
  const count = briefing.value?.new_reports_since || 0;
  return count === 1
    ? "Er is 1 nieuw of gewijzigd rapport sinds deze samenvatting."
    : `Er zijn ${count} nieuwe of gewijzigde rapporten sinds deze samenvatting.`;
});

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString("nl-NL") : "";

const load = async () => {
  if (!props.accountId) return;
  loading.value = true;
  error.value = null;
  rating.value = null;
  try {
    briefing.value = await aiStore.fetchAccountBriefing(props.accountId);
  } catch (e) {
    briefing.value = null;
    error.value = "De samenvatting kon niet worden geladen.";
  } finally {
    loading.value = false;
  }
};

// Quietly re-reads the stored briefing; a failure keeps what is shown.
const refreshStatus = async () => {
  if (!briefing.value || loading.value || generating.value) return;
  try {
    const latest = await aiStore.fetchAccountBriefing(props.accountId);
    if (latest) briefing.value = latest;
  } catch {
    // not worth an error message: the card still shows the stored briefing
  }
};

const generate = async () => {
  generating.value = true;
  error.value = null;
  try {
    briefing.value = await aiStore.generateAccountBriefing(props.accountId);
    rating.value = null;
  } catch (e) {
    // The API client shows the alert; keep the reason visible in the card too.
    error.value =
      e?.response?.data?.message || "De samenvatting kon niet worden gemaakt.";
  } finally {
    generating.value = false;
  }
};

const rate = (value) => {
  rating.value = value;
  aiStore.feedback({ usageLogId: briefing.value.usage_log_id, rating: value });
};

watch(() => props.accountId, load, { immediate: true });
// The account's reports as loaded in the app: when one is added, changed or removed,
// the card checks again whether the summary is still up to date.
const reportsSignature = computed(() =>
  reportsStore.data
    .filter((report) => report.account_id == props.accountId)
    .map((report) => `${report.id}:${(report.description || "").length}`)
    .join(","),
);
watch(reportsSignature, refreshStatus);
</script>

<style scoped>
.ai-briefing__summary {
  white-space: pre-wrap;
}

.ai-briefing__list {
  padding-left: 1.25rem;
}
</style>
